import { Vector2 } from '@babylonjs/core';

import { BMFontChar } from './BMFont';
import { SdfFontAsset } from './SdfFontAsset';

export type SdfTextParagraphOptions = {
  maxWidth: number;
  lineHeight: number;
  letterSpacing: number;
  tabSize: number;
  whiteSpace: /* 'normal' | 'nowrap' | 'pre' | 'pre-wrap' | */ 'pre-line' /* | 'break-spaces'*/;
  textAlign: 'left' | 'right' | 'center' /* | 'justify'*/;
  translate: Vector2 | undefined;
};

export const defaultSdfTextParagraphOptions: SdfTextParagraphOptions = {
  maxWidth: Infinity,
  lineHeight: 1,
  letterSpacing: 1,
  tabSize: 4,
  whiteSpace: 'pre-line',
  textAlign: 'center',
  translate: new Vector2(-0.5, -0.5),
};

export type SdfTextLine = {
  text: string;
  glyphs: SdfGlyph[];
  start: number;
  end: number;
  width: number;
};

export type SdfGlyph = {
  char: BMFontChar;
  /** index of the line */
  line: number;
  /** position within the line */
  position: number;
  x: number;
  y: number;
};

export class SdfTextParagraph {
  public readonly options: SdfTextParagraphOptions;

  get lineHeight() {
    return this.fontAsset.font.common.lineHeight * this.options.lineHeight;
  }

  /** gl y up is flipped */
  readonly paragraph;
  readonly lines;
  readonly width;
  readonly height;
  readonly glyphs;

  constructor(
    public readonly text: string,
    public readonly fontAsset: SdfFontAsset,
    options?: Partial<SdfTextParagraphOptions>
  ) {
    this.options = { ...defaultSdfTextParagraphOptions, ...options };

    const { paragraph, lines, glyphs, width, height } = this.computeMetrics(text);

    this.paragraph = paragraph;
    this.lines = lines;
    this.glyphs = glyphs;
    this.width = width;
    this.height = height;
  }

  private computeMetrics(text: string) {
    const collapsed = this.collapse(text);
    const breaked = this.breakLines(collapsed);
    const trimmed = breaked.map((line) => line.trim());

    const lines = [];
    for (const line of trimmed) {
      lines.push(...this.wrap(line, lines.length));
    }

    const width = Math.max(...lines.map((line) => line.width));
    const height = this.lineHeight * lines.length;

    if (this.options.textAlign !== 'left' || this.options.translate) {
      lines.forEach((line) => {
        const anchor = (() => {
          switch (this.options.textAlign) {
            case 'right':
              return width - line.width;
            case 'center':
              return (width - line.width) / 2;
            case 'left':
            default:
              return 0;
          }
        })();
        const translate = this.options.translate?.multiplyByFloats(width, height);
        line.glyphs.forEach((glyph) => {
          glyph.x += anchor;
          if (translate) {
            glyph.x += translate.x;
            glyph.y += translate.y;
          }
        });
      });
    }

    const glyphs = lines.flatMap((line) => line.glyphs);

    return {
      paragraph: trimmed.join('\n'),
      lines,
      glyphs,
      width,
      height,
    };
  }

  private breakLines(text: string) {
    return text.split('\n');
  }

  private collapse(text: string) {
    return text.replace(/\t/g, ' '.repeat(this.options.tabSize)).replace(/ +/g, ' ');
  }

  private wrap(text: string, lineOffset = 0) {
    const lines = new Array<SdfTextLine>();

    let currentLine = lineOffset;
    let currentGlyphs = new Array<SdfGlyph>();
    let currentCursor = 0;
    let currentWidth = 0;
    let lastChar: BMFontChar | undefined;
    let start = 0;
    let end = start;

    const pushCurrentLine = () => {
      lines.push({
        text: text.slice(start, end),
        glyphs: currentGlyphs,
        start: start,
        end: end,
        width: currentWidth,
      });
    };

    while (end < text.length) {
      const i = end;
      const charCode = text.charCodeAt(i);
      const char = this.fontAsset.getChar(charCode);
      const charWidth = char.width;
      const kerning = lastChar ? this.fontAsset.getKerning(lastChar.id, char.id) : 0;

      currentCursor += kerning + char.xoffset;
      const newWidth = currentCursor + charWidth;
      const cursorProgress = char.xadvance + this.options.letterSpacing;
      const nextPosition = currentCursor + cursorProgress;

      const shouldBreak = nextPosition > this.options.maxWidth || newWidth > this.options.maxWidth;

      if (shouldBreak) {
        pushCurrentLine();

        currentLine++;
        lastChar = undefined;
        currentCursor = 0;
        currentWidth = 0;
        start = end;
        end = start + 1;
        currentGlyphs = [];
      }

      const x = currentCursor;
      const y = currentLine * this.lineHeight + char.yoffset;

      currentGlyphs.push({
        char,
        line: currentLine,
        position: currentGlyphs.length,
        x: x,
        y: y,
      });

      if (!shouldBreak) {
        lastChar = char;
        currentCursor = nextPosition;
        currentWidth = newWidth;
        end++;
      } else {
        currentCursor = cursorProgress;
      }
    }

    if (currentGlyphs.length > 0) {
      pushCurrentLine();
    }

    return lines;
  }
}
